import { useState } from 'react';

import loadable from '@loadable/component';
import BpkButton, {
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import ChevronDownIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-down';
import ChevronUpIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-up';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import withErrorBoundary from '../ErrorBoundary';
import Icon from '../common/Icon';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  AdviceItem,
  AdviceProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/AdviceProps';

import STYLES from './Advice.module.scss';

const AlignedChevronDown = withRtlSupport(ChevronDownIcon);
const AlignedChevronUp = withRtlSupport(ChevronUpIcon);

const MOBILE_INITIAL_COUNT = 3;

const getCardClassName = (
  index: number,
  hasViewMore: boolean,
  isExpanded: boolean,
) => {
  if (hasViewMore && index >= MOBILE_INITIAL_COUNT && !isExpanded) {
    return `${STYLES.card} ${STYLES.cardMobileHidden}`;
  }
  return STYLES.card;
};

type AdviceItemIconProps = {
  icon: AdviceItem['icon'];
};

const AdviceItemIcon = ({ icon }: AdviceItemIconProps) => {
  const IconComponent = Icon(icon);
  return <IconComponent />;
};

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const Advice = ({
  items,
  subtitle,
  title,
  viewLessLabel,
  viewMoreLabel,
}: AdviceProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!items?.length) {
    return null;
  }

  const mbdItemName = `${rawComponentName}_item`;
  const hasViewMore =
    !!viewMoreLabel && !!viewLessLabel && items.length > MOBILE_INITIAL_COUNT;

  return (
    <div
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
      className={STYLES.Advice}
      data-testid="advice"
    >
      <BpkSectionHeader title={title} description={subtitle} />
      <div className={STYLES.grid}>
        {items.map((item, index) => (
          <div
            key={`${item.icon}-${item.title}`}
            data-tracking-element-id={`${mbdItemName}_${index}`}
            data-tracking-common-params={buildMBDParam(mbdItemName)}
            className={getCardClassName(index, hasViewMore, isExpanded)}
          >
            <div className={STYLES.icon}>
              <AdviceItemIcon icon={item.icon} />
            </div>
            <BpkText tagName="h3" textStyle={TEXT_STYLES.heading4}>
              {item.title}
            </BpkText>
            {item.subtitle && (
              <BpkText tagName="h4" textStyle={TEXT_STYLES.heading5}>
                {item.subtitle}
              </BpkText>
            )}
            <BpkText
              textStyle={TEXT_STYLES.bodyDefault}
              dangerouslySetInnerHTML={{ __html: item.body }}
            />
            {item.linkText && item.linkUrl && (
              <BpkText
                tagName="p"
                textStyle={TEXT_STYLES.link}
                className={STYLES.link}
              >
                <BpkLink href={item.linkUrl} rel="nofollow" blank>
                  {item.linkText}
                </BpkLink>
              </BpkText>
            )}
          </div>
        ))}
      </div>
      {hasViewMore && (
        <div className={STYLES.viewMoreContainer}>
          <BpkButton
            type={BUTTON_TYPES.link}
            implicit
            data-tracking-element-id={`${rawComponentName}_cta_${isExpanded ? 'less' : 'more'}`}
            data-tracking-common-params={buildMBDParam(
              `${rawComponentName}_cta`,
              {
                cta_action: isExpanded ? 'less' : 'more',
              },
            )}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <BpkText
              tagName="span"
              textStyle={TEXT_STYLES.label2}
              color={TEXT_COLORS.textLink}
            >
              {isExpanded ? viewLessLabel : viewMoreLabel}
            </BpkText>
            <span className={STYLES.chevron}>
              {isExpanded ? <AlignedChevronUp /> : <AlignedChevronDown />}
            </span>
          </BpkButton>
        </div>
      )}
    </div>
  );
};

const AdviceWrapper = (props: AdviceProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.ADVICE,
      })
    }
  >
    <Advice {...props} />
  </IntersectionObserverWrapper>
);

const AdviceWithErrorBoundary = withErrorBoundary(
  AdviceWrapper,
  'advice-component',
);

export default AdviceWithErrorBoundary;
